import { Dialog } from '@mui/material';
import { getIntegrationStatusByInternalName } from 'lib/api/integrations';
import CustomDialogTitle from 'lib/designSystem/DialogTitle';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import { calculateDays } from 'lib/functions/dates';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getOwnBankAccounts } from '../../../../../../../lib/api/bankAccounts';
import {
  getCompanyConfig,
  triggerScrapOwnBusiness,
} from '../../../../../../../lib/api/company';
import { getCompanyFunds } from '../../../../../../../lib/api/funds';
import {
  createFactoringReSellBatch,
  getDocumentsAvailableForReSelling,
  getDocumentsPreparedForResell,
  updateFactoringReSellBatch,
} from '../../../../../../../lib/api/reSell/factoring';
import { getSelectedCertificate } from '../../../../../../../lib/api/SIICertificates';
import CenteredCircularProgress from '../../../../../../../lib/designSystem/CircularProgress';
import {
  calculateResellSummary,
  formatDate,
  showAlert,
} from '../../../../../../../lib/functions/utils';
import MultiResellDocumentSelector from './components/addDocuments';
import BatchImporter from './components/BatchImporter';
import SimpleCessionDialog from './components/CessionDialog';
import ResellDialogActions from './components/DialogActions';
import ResellsTable from './components/ResellsTable';
import ResellSummary from './components/ResellSummary';
import {
  BankAccount,
  CessionDocument,
  FundInfo,
  ResellDocument,
  ResellSuggestedDocument,
} from './types';

const MultiResellMain = (props: any) => {
  const {
    open,
    setOpen,
    setUp,
    pagination,
    initialResells,
    initialAmountToApply,
    initialFundId,
    initialOperationDate,
    initialApplications,
    initialApplicationsIds,
    nomineeId,
    readOnly = false,
    setReadOnly,
    nominee,
  } = props;
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<any>([]);
  const [documents, setDocuments] = useState<any>([]);
  const [funds, setFunds] = useState<FundInfo[]>([]);
  const [fundId, setFundId] = useState<string>('');
  const [resells, setResells] = useState<ResellDocument[]>([]);
  const [openDocumentSelector, setOpenDocumentSelector] = useState(false);
  const [canForcePaymentDate, setCanForcePaymentDate] = useState(false);
  const [bankAccountId, setBankAccountId] = useState<string>('');
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [selectedCertificate, setSelectedCertificate] = useState<any>(null);
  const [openCessionDialog, setOpenCessionDialog] = useState(false);
  const [hasCessionIntegration, setHasCessionIntegration] = useState(false);
  const [openDocumentBatchImporter, setOpenDocumentBatchImporter] =
    useState(false);
  const [modifiedResells, setModifiedResells] = useState<Set<string>>(
    new Set(),
  );
  const [amountToReceiveInput, setAmountToReceiveInput] = useState<number>(0);
  const [operationDate, setOperationDate] = useState<Date>(
    new Date(new Date().setUTCHours(0, 0, 0, 0)),
  );
  const [amountToApply, setAmountToApply] = useState(0);
  const [selectedResellsToApply, setSelectedResellsToApply] = useState<
    number[]
  >([]);
  const [resellsSuggested, setResellsSuggested] = useState<
    ResellSuggestedDocument[]
  >([]);
  const [resellsToApply, setResellsToApply] = useState<
    ResellSuggestedDocument[]
  >([]);

  const markResellAsModified = (resellId: string) => {
    setModifiedResells((prev) => new Set(prev).add(resellId));
  };

  const markAllResellsAsModified = () => {
    const allResellIds = resells.map((resell: ResellDocument) => resell.id);
    setModifiedResells(new Set(allResellIds));
  };

  interface ValidationRule {
    condition: (resell: ResellDocument) => boolean;
    message: (folio: string) => string;
  }

  interface ApiResellResponse {
    documentId: string;
    folio: string;
    businessIdentifier: string;
    amount: number;
    originalAmount: number;
    stakeholderIdentifier: string;
    subType: string;
    businessName: string;
    stakeholderName: string;
    paymentDate: string;
    requestId: string;
    requestType: string;
    cessionDate?: string;
  }
  // Update resells when initialResells changes (for nominee view)
  useEffect(() => {
    if (initialResells) {
      setResells(initialResells);
      setModifiedResells(new Set());

      if (
        initialResells.length > 0 &&
        initialResells[0].bankAccountReceiverId
      ) {
        setBankAccountId(initialResells[0].bankAccountReceiverId);
      }
    }
  }, [initialResells]);

  useEffect(() => {
    if (initialAmountToApply) {
      setAmountToApply(initialAmountToApply);
    }
  }, [initialAmountToApply]);

  useEffect(() => {
    if (initialFundId) {
      setFundId(initialFundId);
    }
  }, [initialFundId]);

  useEffect(() => {
    if (nominee) {
      if (nominee.amountToReceive) {
        setAmountToReceiveInput(nominee.amountToReceive);
      } else {
        const summary = calculateResellSummary(resells, amountToApply);
        const calculatedAmountToReceive = summary.amountToReceive;

        const initialValue =
          nominee.amountToReceive !== undefined &&
          nominee.amountToReceive !== null &&
          nominee.amountToReceive > 0
            ? nominee.amountToReceive
            : calculatedAmountToReceive;

        setAmountToReceiveInput(initialValue);
      }
    }
  }, [nominee]);

  useEffect(() => {
    if (initialOperationDate) {
      setOperationDate(initialOperationDate);
    }
  }, [initialOperationDate]);

  useEffect(() => {
    if (initialApplications) {
      setResellsToApply(initialApplications);
    }
  }, [initialApplications]);

  useEffect(() => {
    if (initialApplicationsIds) {
      setSelectedResellsToApply(initialApplicationsIds);
    }
  }, [initialApplicationsIds]);

  const validParams = () => {
    const validations: ValidationRule[] = [
      {
        condition: (resell: ResellDocument) => !resell.startDate,
        message: (folio: string) =>
          `La factura con folio ${folio} no tiene una fecha de inicio`,
      },
      {
        condition: (resell: ResellDocument) => !resell.endDate,
        message: (folio: string) =>
          `La factura con folio ${folio} no tiene una fecha de término`,
      },
      {
        condition: (resell: ResellDocument) => !resell.cessionDate,
        message: (folio: string) =>
          `La factura con folio ${folio} no tiene una fecha de cesión`,
      },
      {
        condition: (resell: ResellDocument) =>
          resell.startDate === resell.endDate,
        message: (folio: string) =>
          `La factura con folio ${folio} tiene una fecha de inicio igual a la fecha de término`,
      },
      {
        condition: (resell: ResellDocument) =>
          resell.startDate > resell.endDate,
        message: (folio: string) =>
          `La factura con folio ${folio} tiene una fecha de inicio mayor a la fecha de término`,
      },
    ];

    for (const resell of resells) {
      if (resell.deleted) {
        continue;
      }
      for (const validation of validations) {
        if (validation.condition(resell)) {
          showAlert(dispatch, 'error', validation.message(resell.folio));
          return false;
        }
      }
    }
    if (!fundId) {
      showAlert(dispatch, 'error', 'Debes seleccionar un fondo');
      return false;
    }
    return true;
  };

  const handleOpenCessionDialog = () => {
    if (!validParams()) {
      return;
    }
    setOpenCessionDialog(true);
  };

  const getDocumentsData = async (
    documentIds: string[],
  ): Promise<ResellDocument[]> => {
    try {
      const response = await getDocumentsPreparedForResell(
        dispatch,
        documentIds,
      );
      const newResells: ResellDocument[] = response.map(
        (resell: ApiResellResponse) => ({
          id: resell.documentId,
          folio: resell.folio,
          originalAmount: resell.originalAmount,
          advance: 100,
          arrearRate: 0,
          businessIdentifier: resell.businessIdentifier,
          requestId: resell.requestId,
          requestType: resell.requestType,
          amount: resell.amount,
          stakeholderIdentifier: resell.stakeholderIdentifier,
          businessName: resell.businessName,
          stakeholderName: resell.stakeholderName,
          paymentDate: formatDate(new Date(resell.paymentDate)),
          cessionDate: resell.cessionDate
            ? formatDate(new Date(resell.cessionDate))
            : null,
          tempStartDate: formatDate(new Date()),
          startDate: formatDate(new Date()),
          subType: resell.subType,
          tempEndDate: formatDate(new Date(resell.paymentDate)),
          endDate: formatDate(new Date(resell.paymentDate)),
          days: calculateDays(
            new Date(formatDate(new Date())),
            new Date(formatDate(new Date(resell.paymentDate))),
          ),
          operationCostFixed: 0,
          operationCostVariable: 0,
          rate: 0,
          rateCost: 0,
          observation: '',
          deleted: false,
        }),
      );
      return newResells;
    } catch (error) {
      return [];
    }
  };
  const handleSearchDataInBatchImporter = async (
    documentsSelected: { id: string; dueDate?: string }[] = [],
  ) => {
    setLoading(true);
    setOpenDocumentSelector(false);
    try {
      const ids = documentsSelected.map((d) => d.id);
      const idToDueDate = documentsSelected.reduce<
        Record<string, string | undefined>
      >((acc, curr) => {
        acc[curr.id] = curr.dueDate;
        return acc;
      }, {});

      const newResells = await getDocumentsData(ids);

      const adjustedResells = newResells.map((resell) => {
        const excelDueDate = idToDueDate[resell.id];
        if (excelDueDate) {
          const formattedEnd = formatDate(new Date(excelDueDate));
          const newDays = calculateDays(
            new Date(resell.startDate),
            new Date(formattedEnd),
          );
          return {
            ...resell,
            tempEndDate: formattedEnd as any,
            endDate: formattedEnd as any,
            days: newDays,
          } as ResellDocument;
        }
        return resell;
      });

      const oldResells = [...resells];
      const existingIds = new Set(oldResells.map((resell) => resell.id));
      const uniqueNewResells = adjustedResells.filter(
        (resell) => !existingIds.has(resell.id),
      );

      setResells([...oldResells, ...uniqueNewResells]);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const handleSearchData = async () => {
    setLoading(true);
    setOpenDocumentSelector(false);
    try {
      const newResells: ResellDocument[] = await getDocumentsData(documents);
      const oldResells: ResellDocument[] = [...resells];

      const existingIds = new Set(oldResells.map((resell) => resell.id));
      const uniqueNewResells = newResells.filter(
        (resell) => !existingIds.has(resell.id),
      );

      setResells([...oldResells, ...uniqueNewResells]);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const handleSubmit = async (documentsIds: string[] = []) => {
    if (!validParams()) {
      return;
    }
    const documentsToResell = resells;
    if (!documentsToResell.length) {
      showAlert(
        dispatch,
        'error',
        'No se registró la venta de facturas. Si es un error, te pedimos que la registres manualmente. ',
      );
      return;
    }

    setLoading(true);
    try {
      const resellsPayload = documentsToResell.map(
        (resell: ResellDocument) => ({
          documentId: resell.id,
          rate: Number(resell.rate),
          rateCost: Number(resell.rateCost),
          amount: Number(resell.amount),
          advance: Number(resell.advance),
          arrearRate: Number(resell.arrearRate),
          endDate: new Date(resell.endDate),
          fundId: fundId,
          operationCostFixed: Number(resell.operationCostFixed) || 0,
          operationCostVariable: Number(resell.operationCostVariable) || 0,
          startDate: new Date(resell.startDate),
          bankAccountReceiverId: bankAccountId,
          cessionByPruff: false,
        }),
      );
      const resellToApplyPayload = resellsToApply.map(
        (resell: ResellSuggestedDocument) => ({
          resellId: resell.id,
          amount: Number(resell.amountToConciliate || 0),
        }),
      );
      await createFactoringReSellBatch(dispatch, {
        resells: resellsPayload,
        amountToReceive: amountToReceiveInput,
        operationDate: operationDate,
        amountToApply: amountToApply,
        resellsToApply: resellToApplyPayload,
      });
      setResells([]);
      setResellsToApply([]);
      setResellsSuggested([]);
      setSelectedResellsToApply([]);
      setAmountToApply(0);
      showAlert(dispatch, 'success', 'Venta ejecutada correctamente');
      if (!documentsIds.length) {
        setOpen(false);
      }
      await setUp({ page: pagination.page, pageSize: pagination.pageSize });
    } catch (error: any) {
      showAlert(dispatch, 'error', 'Error al ejecutar la venta');
    }
    setLoading(false);
  };

  const handleUpdateNomineeResells = async () => {
    if (!validParams()) {
      return;
    }
    const documentsToUpdate = nomineeId
      ? resells.filter((resell: any) => modifiedResells.has(resell.id))
      : resells;

    const amountChanged = amountToReceiveInput !== nominee?.amountToReceive;
    if (!documentsToUpdate.length && !amountChanged) {
      showAlert(dispatch, 'warning', 'No hay cambios para guardar.');
      return;
    }

    setLoading(true);
    try {
      const batchPayload = {
        nomineeId,
        amountToReceive: amountToReceiveInput,
        resells: documentsToUpdate.map((resell: any) => ({
          id: resell.id,
          data: {
            rate: parseFloat(resell.rate),
            rateCost: parseFloat(resell.rateCost),
            amount: parseFloat(resell.amount),
            advance: parseFloat(resell.advance),
            arrearRate: parseFloat(resell.arrearRate),
            dueDate: new Date(resell.endDate),
            fundId: fundId,
            operationCostFixed: Number(resell.operationCostFixed) || 0,
            operationCostVariable: Number(resell.operationCostVariable) || 0,
            startDate: new Date(resell.startDate),
            bankAccountReceiverId: bankAccountId,
            deleted: resell.deleted,
          },
        })),
      };

      await updateFactoringReSellBatch(dispatch, batchPayload);

      showAlert(
        dispatch,
        'success',
        nomineeId
          ? `${documentsToUpdate.length} ventas actualizadas correctamente`
          : 'Nómina creada correctamente',
      );
      setOpen(false);
      setModifiedResells(new Set());

      await new Promise((resolve) => setTimeout(resolve, 500));

      if (setUp) {
        try {
          await setUp({ page: pagination.page, pageSize: pagination.pageSize });
        } catch (setUpError) {
          console.error('Error refreshing page data:', setUpError);
        }
      }

      setReadOnly?.(true);
    } catch (error: any) {
      console.error('Error updating nominee resells:', error);
      showAlert(dispatch, 'error', 'Error al actualizar la nómina');
      setReadOnly?.(true);
    } finally {
      setLoading(false);
    }
  };

  const setUpMainPage = async () => {
    await setUp({ page: pagination.page, pageSize: pagination.pageSize });
  };

  const setUpSuggestions = async () => {
    try {
      const response = await getDocumentsAvailableForReSelling(dispatch);
      setSuggestions(response.documentsProcessed);
      const fundsResponse = await getCompanyFunds(dispatch);
      setFunds(fundsResponse);
    } catch (error) {
      console.error(error);
    }
  };

  const setUpConfig = async () => {
    try {
      const response = await getCompanyConfig(
        dispatch,
        'system_operations_fund_forcePaymentDate',
      );
      if (response === '1') {
        setCanForcePaymentDate(true);
      }
      const bankAccountsResponse = await getOwnBankAccounts(dispatch);
      setBankAccounts(bankAccountsResponse);
    } catch (error) {
      console.error('Error fetching date restrictions config', error);
    }
  };

  const setUpCertificates = async () => {
    try {
      const response = await getSelectedCertificate(dispatch);
      setSelectedCertificate(response);
    } catch (error) {
      console.error(error);
    }
  };

  const checkCessionIntegration = async () => {
    try {
      const response = await getIntegrationStatusByInternalName(
        dispatch,
        'recesion',
      );
      setHasCessionIntegration(response.status);
    } catch (error) {
      console.error(error);
    }
  };

  const setUpDialog = async () => {
    setLoading(true);
    await setUpSuggestions();
    await setUpConfig();
    await setUpCertificates();
    await checkCessionIntegration();
    setLoading(false);
  };

  useEffect(() => {
    void setUpDialog();
  }, []);

  const sendToScrapCession = async () => {
    try {
      void triggerScrapOwnBusiness(dispatch);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (hasCessionIntegration) {
      void sendToScrapCession();
    }
  }, [hasCessionIntegration]);

  const suggestionsAvailables = suggestions.filter(
    (suggestion: any) =>
      !resells.some((resell: ResellDocument) => resell.id === suggestion.id),
  );

  return (
    <Dialog maxWidth="xl" fullWidth open={open}>
      <div style={{ padding: 20, minHeight: 200 }}>
        {loading ? (
          <CenteredCircularProgress type="dialog" />
        ) : (
          <div>
            <HeaderWithActions
              title={nomineeId ? `Nómina #${nomineeId}` : 'Crear nómina '}
              type="dialogHeader"
            />
            <ResellSummary
              resells={resells}
              setResells={setResells}
              bankAccountId={bankAccountId}
              setBankAccountId={setBankAccountId}
              bankAccounts={bankAccounts}
              amountToReceiveInput={amountToReceiveInput}
              setAmountToReceiveInput={setAmountToReceiveInput}
              nomineeId={nomineeId}
              readOnly={readOnly}
              markAllResellsAsModified={markAllResellsAsModified}
              funds={funds}
              fundId={fundId}
              setFundId={setFundId}
              operationDate={operationDate}
              setOperationDate={setOperationDate}
              amountToApply={amountToApply}
              setAmountToApply={setAmountToApply}
              selectedResellsToApply={selectedResellsToApply}
              setSelectedResellsToApply={setSelectedResellsToApply}
              resellsSuggested={resellsSuggested}
              setResellsSuggested={setResellsSuggested}
              resellsToApply={resellsToApply}
              setResellsToApply={setResellsToApply}
            />
            <div style={{ padding: '10px 0px' }}>
              <ResellsTable
                resells={resells}
                setResells={setResells}
                funds={funds}
                canForcePaymentDate={canForcePaymentDate}
                readOnly={readOnly}
                markResellAsModified={markResellAsModified}
                isNomineeView={!!nomineeId}
                setOpenDocumentSelector={setOpenDocumentSelector}
                setOpenDocumentBatchImporter={setOpenDocumentBatchImporter}
              />
            </div>
            <ResellDialogActions
              readOnly={readOnly}
              nomineeId={nomineeId}
              resells={resells}
              bankAccountId={bankAccountId}
              selectedCertificate={selectedCertificate}
              hasCessionIntegration={hasCessionIntegration}
              onEdit={() => {
                props.setReadOnly?.(false);
              }}
              onClose={() => {
                setOpen(false);
                if (readOnly) {
                  setResells([]);
                }
              }}
              onSave={handleUpdateNomineeResells}
              onConfirm={() => handleSubmit()}
              onConfirmAndCede={handleOpenCessionDialog}
            />
          </div>
        )}
      </div>
      {openCessionDialog && (
        <SimpleCessionDialog
          open={openCessionDialog}
          setOpen={setOpenCessionDialog}
          bankAccountId={Number(bankAccountId)}
          amountToReceiveInput={amountToReceiveInput}
          documents={resells.map(
            (resell: ResellDocument): CessionDocument => ({
              id: resell.id,
              businessName: resell.businessName,
              businessIdentifier: resell.businessIdentifier,
              folio: resell.folio,
              fundId: fundId,
              fundName: resell.fundName,
              amount: resell.amount,
              advance: resell.advance,
              arrearRate: resell.arrearRate,
              originalAmount: resell.originalAmount,
              stakeholderIdentifier: resell.stakeholderIdentifier,
              stakeholderName: resell.stakeholderName,
              paymentDate: resell.paymentDate,
              cessionDate: resell.cessionDate,
              startDate: resell.startDate,
              endDate: resell.endDate,
              rate: resell.rate,
              rateCost: resell.rateCost,
              operationCostFixed: resell.operationCostFixed,
              operationCostVariable: resell.operationCostVariable,
              subType: resell.subType ?? '33',
            }),
          )}
          funds={funds}
          setUpMainPage={setUpMainPage}
        />
      )}
      {openDocumentSelector && (
        <MultiResellDocumentSelector
          suggestions={suggestionsAvailables}
          documents={documents}
          setDocuments={setDocuments}
          handleConfirm={handleSearchData}
          open={openDocumentSelector}
          setOpen={setOpenDocumentSelector}
        />
      )}
      {openDocumentBatchImporter && (
        <BatchImporter
          open={openDocumentBatchImporter}
          setOpen={setOpenDocumentBatchImporter}
          suggestions={suggestionsAvailables}
          onDocumentsSelected={handleSearchDataInBatchImporter}
        />
      )}
    </Dialog>
  );
};

export default MultiResellMain;
